import { useEffect, useState, type ReactNode } from "react";
import { useUser } from "@/hooks/useUserProvider";
import { request } from "@/lib/request";
import { UserSocial } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Label } from "../../ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Loader2Icon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type IntegrationCodeRecord = {
  id: string;
  code: string | null;
  name: string;
  createdAt: string;
  userSocial: UserSocial | null;
};

type IntegrationCodesResponse = {
  data: IntegrationCodeRecord[];
};

type IntegrationCodeResponse = {
  data: IntegrationCodeRecord;
};

const integrationCodeSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type IntegrationCodeFormValues = z.infer<typeof integrationCodeSchema>;

type IntegrationCodeFormDialogProps = {
  mode: "create" | "edit";
  trigger: ReactNode;
  social: UserSocial | null;
  integrationCode?: IntegrationCodeRecord;
  onSuccess: (code: IntegrationCodeRecord, revealed?: string) => void;
};

const IntegrationCodeFormDialog = ({
  mode,
  trigger,
  social,
  integrationCode,
  onSuccess,
}: IntegrationCodeFormDialogProps) => {
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const form = useForm<IntegrationCodeFormValues>({
    resolver: zodResolver(integrationCodeSchema),
    defaultValues: {
      name: integrationCode?.name ?? "",
    },
  });

  useEffect(() => {
    form.reset({
      name: integrationCode?.name ?? "",
    });
  }, [integrationCode, form, open]);

  const mutation = useMutation({
    mutationFn: async (values: IntegrationCodeFormValues) => {
      if (mode === "create") {
        if (!social) {
          throw new Error("You need to select an account first.");
        }

        const response = await request
          .post<IntegrationCodeResponse>("/user/integration-codes", {
            name: values.name,
            userSocialId: social.id,
          })
          .catch(err => {
            throw new Error(
              err.response?.data?.message ?? "Failed to create access code.",
            );
          });

        return response.data.data;
      }

      if (!integrationCode) {
        throw new Error("Unable to update this access code.");
      }

      const response = await request
        .put<{
          data: IntegrationCodeRecord;
        }>(`/user/integration-codes/${integrationCode.id}`, {
          name: values.name,
        })
        .catch(err => {
          throw new Error(
            err.response?.data?.message ?? "Failed to update access code.",
          );
        });

      return response.data.data;
    },
    onSuccess: async data => {
      onSuccess(
        data,
        mode === "create" ? (data?.code ?? undefined) : undefined,
      );
      await queryClient.invalidateQueries({ queryKey: ["integration-codes"] });
      setOpen(false);
    },
    onError: error => {
      setSubmitError(
        error instanceof Error ? error.message : "An error occurred",
      );
    },
  });

  const handleSubmit = (values: IntegrationCodeFormValues) => {
    setSubmitError(null);
    mutation.mutate(values);
  };

  const isCreate = mode === "create";

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          setSubmitError(null);
          mutation.reset();
        }
        setOpen(nextOpen);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isCreate ? "Create access code" : "Edit access code"}
          </DialogTitle>
          <DialogDescription>
            {isCreate
              ? "Generate a new Bitrix24 access code for the selected account."
              : "Update the access code name."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <div className="flex flex-col gap-2">
              <Label className="text-base font-bold">Account</Label>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-sm font-semibold text-muted-foreground">
                  Email:
                </span>
                {social?.email ?? integrationCode?.userSocial?.email ?? "-"}
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-sm font-semibold text-muted-foreground">
                  Bitrix24 Address:
                </span>
                {social?.metadata?.client_endpoint ?? "-"}
              </div>
            </div>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Marketing" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {submitError ? (
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setOpen(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mutation.isPending || (isCreate && !social)}
              >
                {mutation.isPending ? (
                  <>
                    <Loader2Icon className="me-2 animate-spin" />
                    Saving
                  </>
                ) : isCreate ? (
                  <>
                    <PlusIcon className="me-2 size-4" />
                    Create
                  </>
                ) : (
                  <>
                    <PencilIcon className="me-2 size-4" />
                    Save
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const Bitrix24ManageAccessDialog = ({
  social,
}: {
  social: UserSocial;
}) => {
  const { accessToken } = useUser();
  const [open, setOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] =
    useState<IntegrationCodeRecord | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [revealedCodes, setRevealedCodes] = useState<Record<string, string>>(
    {},
  );
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["integration-codes", { accessToken }],
    enabled: open && !!accessToken,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response = await request
        .get<IntegrationCodesResponse>("/user/integration-codes")
        .catch(err => {
          throw new Error(
            err.response?.data?.message ?? "Failed to load access codes.",
          );
        });

      return response.data.data;
    },
  });

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
  };

  const handleDelete = async () => {
    if (!codeToDelete) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await request
        .delete(`/user/integration-codes/${codeToDelete.id}`)
        .catch(err => {
          throw new Error(
            err.response?.data?.message ?? "Failed to delete access code.",
          );
        });

      await queryClient.invalidateQueries({ queryKey: ["integration-codes"] });
      setRevealedCodes(prev => {
        const next = { ...prev };
        delete next[codeToDelete.id];
        return next;
      });
      setCodeToDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const handleDialogChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setRevealedCodes({});
      setCodeToDelete(null);
      setDeleteError(null);
    }
    setOpen(nextOpen);
  };

  const handleFormSuccess = (
    code: IntegrationCodeRecord,
    revealed?: string,
  ) => {
    if (revealed) {
      setRevealedCodes(prev => ({ ...prev, [code.id]: revealed }));
    }
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [copied]);

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="default">
          Manage access
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Manage Bitrix24 access codes</DialogTitle>
          <DialogDescription>
            View, create, update, or revoke access codes associated with your
            Bitrix24 integration.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label className="text-base font-bold">Account</Label>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-sm font-semibold text-muted-foreground">
              Email:
            </span>
            {social?.email ?? "-"}
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-sm font-semibold text-muted-foreground">
              Bitrix24 Address:
            </span>
            {social?.metadata?.client_endpoint ?? "-"}
          </div>
        </div>
        <div className="flex flex-col gap-6">
          {isError ? (
            <Alert variant="destructive">
              <AlertTitle>Failed to fetch access codes</AlertTitle>
              <AlertDescription>
                {error instanceof Error
                  ? error.message
                  : "An unknown error occurred."}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading || isFetching ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">
                      <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                        <Loader2Icon className="animate-spin" />
                        Loading access codes...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : data?.length ? (
                  data.map(code => {
                    const revealed = revealedCodes[code.id];

                    return (
                      <TableRow key={code.id}>
                        <TableCell className="font-medium">
                          {code.name}
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn("font-mono text-sm", {
                              "cursor-pointer rounded bg-transparent p-1 hover:bg-gray-500/50":
                                revealed,
                            })}
                            onClick={() =>
                              revealed ? copyCode(revealed) : null
                            }
                          >
                            {copied ? "Copied" : (revealed ?? "********")}
                          </span>
                        </TableCell>
                        <TableCell>
                          {code.createdAt
                            ? format(new Date(code.createdAt), "yyyy-MM-dd")
                            : "-"}
                        </TableCell>
                        <TableCell className="flex items-center justify-end gap-2">
                          <IntegrationCodeFormDialog
                            mode="edit"
                            trigger={
                              <Button variant="ghost" size="icon">
                                <PencilIcon />
                              </Button>
                            }
                            social={social}
                            integrationCode={code}
                            onSuccess={handleFormSuccess}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setCodeToDelete(code);
                              setDeleteError(null);
                            }}
                          >
                            <TrashIcon />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No access codes yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
            <IntegrationCodeFormDialog
              mode="create"
              trigger={
                <Button className="self-start sm:self-auto" size="sm">
                  <PlusIcon className="me-2 size-4" />
                  New access code
                </Button>
              }
              social={social}
              onSuccess={handleFormSuccess}
            />
          </div>
        </div>
      </DialogContent>

      <AlertDialog
        open={!!codeToDelete}
        onOpenChange={nextOpen => {
          if (!nextOpen && !deleting) {
            setCodeToDelete(null);
            setDeleteError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete access code?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will revoke the selected access code. Any service
              using it will stop working until a new code is generated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {codeToDelete ? (
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              <div className="font-medium">{codeToDelete.name}</div>
              <div className="text-muted-foreground">
                {codeToDelete.userSocial?.email}
              </div>
            </div>
          ) : null}
          {deleteError ? (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="flex items-center gap-2 bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <TrashIcon className="size-4" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
